import { useEffect, useRef, useCallback } from "react";
import InputNew from "components/Form/newAuthInput";
import PhoneInput from "components/atoms/PhoneInput/newAuthPhoneInput";
import Button from "components/Form/Button";
import Link from "next/link";
import { useLang } from "hooks/useLang";
import useSignup from "hooks/useSignup";
import { useRouter } from "next/navigation";
// Removed nationality/gender/DOB fields per new requirements

interface SignupProps {
  onSwitchToLogin?: () => void;
  setStep?: (step: string) => void;
  setAuthData?: (data: any) => void;
  setAuthMode?: (mode: "login" | "signup") => void;
  onSignupSuccess?: () => void;
  isRedirect?: boolean;
  isModalView?: boolean;
}

const SignUpForm = ({
  onSwitchToLogin,
  setStep,
  setAuthData,
  setAuthMode,
  isRedirect = false,
}: SignupProps) => {
  const { t } = useLang();
  const router = useRouter();
  const formRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const setRef = useCallback(
    (field: string) => (el: HTMLDivElement | null) => {
      formRefs.current[field] = el;
    },
    []
  );

  const handleSignupSuccess = () => {
    if (setAuthData && setStep && setAuthMode) {
      const phoneVal = form.getFieldValue("phone");
      const emailVal = form.getFieldValue("email");
      const callingCodeVal = form.getFieldValue("callingCode");
      setAuthData({
        inputValue: phoneVal || emailVal,
        inputType: phoneVal ? "phone" : "email",
        callingCode: callingCodeVal,
        email: emailVal.toLowerCase(),
      });
      setStep("ver");
      setAuthMode("signup");
    }
  };

  const {
    form,
    fieldValidators,
    serverErrors,
    isLoading,
    setServerErrors,
  } = useSignup({ onSignupSuccess: handleSignupSuccess, isRedirect });

  useEffect(() => {
    setServerErrors({});
  }, [setServerErrors]);

  const scrollToError = () => {
    const order = ["first_name", "last_name", "email", "phone", "password"] as const;
    for (const key of order) {
      const meta = form.getFieldMeta(key);
      const hasClientErr = Array.isArray(meta?.errors) && meta!.errors.length > 0;
      const hasServerErr = !!(serverErrors as any)[key];
      if (hasClientErr || hasServerErr) {
        const el = formRefs.current[key];
        if (el?.scrollIntoView) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        break;
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerErrors({});
    await form.handleSubmit();
    // Scroll to the first error (if any)
    setTimeout(() => scrollToError(), 50);
  };

  const handleLoginInsteadClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    if (onSwitchToLogin && setAuthMode) {
      onSwitchToLogin();
      setAuthMode("login");
    } else {
      router.push("/user/login");
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <form className="flex flex-col gap-3" onSubmit={handleSubmit}>
        <div className="flex gap-x-5">
          <form.Field
            name="first_name"
            validators={{ onChange: fieldValidators.first_name }}
            children={(field) => (
              <InputNew
                ref={setRef("first_name")}
                label={t("lb_First_Name")}
                inputID="first_name_signup"
                name="first_name"
                type="text"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value.trim())}
                onBlur={field.handleBlur}
                placeholder={t("lb_First_Name")}
                value={field.state.value}
                error={(field.state.meta.errors?.[0] as any)?.message ?? (field.state.meta.errors?.[0] as any) ?? ""}
                reqStar
                inputContainerClassName="h-[40px]"
              />
            )}
          />
          <form.Field
            name="last_name"
            validators={{ onChange: fieldValidators.last_name }}
            children={(field) => (
              <InputNew
                ref={setRef("last_name")}
                label={t("lb_Last_Name")}
                inputID="last_name_signup"
                name="last_name"
                type="text"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value.trim())}
                onBlur={field.handleBlur}
                placeholder={t("lb_Last_Name")}
                value={field.state.value}
                error={(field.state.meta.errors?.[0] as any)?.message ?? (field.state.meta.errors?.[0] as any) ?? ""}
                reqStar
                inputContainerClassName="h-[40px]"
              />
            )}
          />
        </div>

        <form.Field
          name="email"
          validators={{ onChange: fieldValidators.email, onBlur: fieldValidators.email }}
          children={(field) => (
            <InputNew
              ref={setRef("email")}
              label={t("lb_email")}
              inputID="email_signup"
              name="email"
              type="email"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value.trim())}
              onBlur={field.handleBlur}
              placeholder={t("lb_email")}
              value={field.state.value}
              error={((field.state.meta.errors?.[0] as any)?.message ?? (field.state.meta.errors?.[0] as any) ?? "") || serverErrors.email}
              reqStar
              inputContainerClassName="h-[40px]"
            />
          )}
        />
        <form.Field
          name="phone"
          validators={{ onChange: fieldValidators.phone }}
          children={(phoneField) => (
            <form.Field
              name="callingCode"
              validators={{ onChange: fieldValidators.callingCode }}
              children={(codeField) => (
                <PhoneInput
                  ref={setRef("phone")}
                  callingCode={codeField.state.value}
                  setCallingCode={(callingCode: string) => codeField.handleChange(callingCode.trim())}
                  setPhone={(phone: string) => phoneField.handleChange(phone.trim())}
                  phone={phoneField.state.value}
                  error={((phoneField.state.meta.errors?.[0] as any)?.message ?? (phoneField.state.meta.errors?.[0] as any) ?? "") || serverErrors.phone}
                  required
                  inputContainerClassName="h-[40px]"
                  placeholder={t("phoneNumber")}
                />
              )}
            />
          )}
        />

        {/* Nationality removed */}

        {/* Gender removed */}

        {/* Date of Birth removed */}

        <form.Field
          name="password"
          validators={{ onChange: fieldValidators.password }}
          children={(field) => (
            <InputNew
              ref={setRef("password")}
              placeholder={t("password")}
              label={t("password")}
              inputID="password_signup"
              name="password"
              type="password"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => field.handleChange(e.target.value)}
              value={field.state.value}
              // Hide raw translation key like "password_valid_err"; show requirements instead
              error={""}
              reqStar
              // Show better hint (requirements) only when invalid
              showPasswordRequirements={Boolean(field.state.meta.errors?.length)}
              inputContainerClassName="h-[40px]"
            />
          )}
        />

        <div className="px-2 mt-4">
          <p className="text-xs text-gray-600 dark:text-gray-200 md:text-sm mb-2">
            {t("user_signup_terms_notice")}&nbsp;
            <Link
              href="/support/terms-of-use"
              passHref
              className="text-[#0DB9A0]"
              target="_blank"
            >
              {t("USER_TAC_TITLE")}
            </Link>
          </p>
          <div className="w-full bg-[#F0F0F033] h-[1px] mb-2"></div>
        </div>

        <div className="flex justify-between items-center w-full flex-col gap-4">
          {/* <div className="flex flex-col">
                        <p className="text-sm text-gray-600 dark:text-gray-200 md:text-lg">
                            {t("have_account")}
                        </p>
                        <Link
                            onClick={handleLoginInsteadClick}
                            href={"/user/login"}
                            passHref
                            className="text-[#0DB9A0] text-sm md:text-lg"
                        >
                            {t("btn_login")}
                        </Link>
                    </div> */}
          <form.Field name="password">
            {(pwdField) => (
              <Button
                text={t("continue")}
                loading={isLoading}
                disabled={
                  isLoading || Boolean(pwdField.state.meta.errors?.length)
                }
                type="submit"
                theme="modern"
                className="p-2 bg-[#1DC2AB] w-full  text-[#000]"
              />
            )}
          </form.Field>
          <Button
            disabled={isLoading}
            text={t("btn_login")}
            onClick={handleLoginInsteadClick}
            theme="modern"
            className="p-2 bg-transparent border-[#1DC2AB] w-full"
          />
        </div>
      </form>
    </div>
  );
};

export default SignUpForm;
